import {
  AGENT_RUN_STATUS_LABELS,
  formatDateTime,
  formatRelative,
  type AgentDto,
  type DashboardSummaryDto,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CheckCircle2, Inbox, Radar } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFilterParams } from '../app/FilterProvider.js';
import { EmptyState, ErrorState } from '../components/EmptyState.js';
import { GlassPanel, PanelHeader } from '../components/GlassPanel.js';
import { NovaOrb } from '../components/NovaOrb.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { CountTile, StatCard } from '../components/StatCard.js';
import { BriefPanel } from '../features/dashboard/BriefPanel.js';
import { ContentDrawer } from '../features/content/ContentDrawer.js';
import { ContentRow } from '../features/content/ContentRow.js';
import { SignalsPanel } from '../features/signals/SignalsPanel.js';
import { api, query } from '../lib/api.js';
import { cn } from '../lib/cn.js';

export function DashboardPage() {
  const params = useFilterParams();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);

  // Clicking a status tile opens the editorial plan already filtered to it, so
  // «3 forslag» takes you straight to those three cases.
  const openPlan = (q: string) => navigate(`/plan?${q}`);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', params],
    queryFn: () => api.get<DashboardSummaryDto>(`/dashboard/summary${query(params)}`),
    refetchInterval: 30_000,
  });

  if (isError) {
    return (
      <GlassPanel>
        <ErrorState onRetry={() => void refetch()} />
      </GlassPanel>
    );
  }

  return (
    <div className="space-y-4">
      <BriefPanel
        brief={data?.brief ?? null}
        theme={data?.currentTheme ?? null}
        status={data?.systemStatus ?? 'OK'}
        loading={isLoading}
      />

      {/* Status row — the same numbers drive the mobile status screen. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <CountTile
          label="Forsinket"
          value={data?.counts.overdue ?? 0}
          tone="danger"
          onClick={() => openPlan('overdue=1')}
        />
        <CountTile
          label="Forslag"
          value={data?.counts.SUGGESTED ?? 0}
          tone="violet"
          onClick={() => openPlan('status=SUGGESTED')}
        />
        <CountTile
          label="I arbeid"
          value={data?.counts.DRAFT ?? 0}
          tone="neutral"
          onClick={() => openPlan('status=DRAFT')}
        />
        <CountTile
          label="Planlagt"
          value={data?.counts.SCHEDULED ?? 0}
          tone="warning"
          onClick={() => openPlan('status=SCHEDULED')}
        />
        <CountTile
          label="Publisert"
          value={data?.counts.PUBLISHED ?? 0}
          tone="accent"
          onClick={() => openPlan('status=PUBLISHED')}
        />
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => <SkeletonPanel key={i} lines={2} />)
          : data?.kpis
              .slice(0, 4)
              .map((kpi) => (
                <StatCard
                  key={kpi.id}
                  label={kpi.name}
                  value={kpi.currentValue ?? 0}
                  unit={kpi.unit}
                  previous={kpi.previousValue}
                  target={kpi.targetValue}
                  lowerIsBetter={kpi.unit === 'timer' || kpi.unit === 'dager'}
                  accentColor={kpi.area?.color ?? undefined}
                  series={kpi.datapoints?.map((d) => ({ value: d.value }))}
                />
              ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <GlassPanel padded={false}>
            <div className="px-5 pt-5">
              <PanelHeader
                title="Krever handling"
                subtitle="Forsinkelser, forslag til vurdering og utkast som venter på deg"
                icon={<AlertTriangle size={16} />}
                action={
                  <Link to="/plan" className="text-accent text-xs hover:underline">
                    Se hele planen
                  </Link>
                }
              />
            </div>
            <div className="space-y-1 px-3 pb-3">
              {isLoading ? (
                <div className="space-y-2 p-2">
                  <SkeletonPanel lines={1} />
                  <SkeletonPanel lines={1} />
                </div>
              ) : data?.needsAction.length ? (
                data.needsAction.map((item) => (
                  <ContentRow key={item.id} item={item} onOpen={(i) => setOpenId(i.id)} />
                ))
              ) : (
                <EmptyState
                  icon={<CheckCircle2 size={26} strokeWidth={1.5} />}
                  title="Ingenting venter på deg akkurat nå."
                  description="Slik skal det se ut."
                />
              )}
            </div>
          </GlassPanel>

          <GlassPanel padded={false}>
            <div className="px-5 pt-5">
              <PanelHeader
                title="Kommende publiseringer"
                subtitle="Neste to uker"
                icon={<CalendarClock size={16} />}
              />
            </div>
            <div className="space-y-1 px-3 pb-3">
              {data?.upcoming.length ? (
                data.upcoming.map((item) => (
                  <ContentRow key={item.id} item={item} onOpen={(i) => setOpenId(i.id)} compact />
                ))
              ) : (
                <EmptyState title="Ingen publiseringer planlagt de neste to ukene." />
              )}
            </div>
          </GlassPanel>
        </div>

        <div className="space-y-4">
          <GlassPanel padded={false}>
            <div className="px-5 pt-5">
              <PanelHeader
                title="Agentstatus"
                subtitle="Hva Nova gjør, og når neste kjøring er"
                icon={<NovaOrb size={7} animate={false} />}
                action={
                  <Link to="/agenter" className="text-accent text-xs hover:underline">
                    Alle agenter
                  </Link>
                }
              />
            </div>
            <ul className="space-y-1 px-3 pb-3">
              {data?.agents.map((agent) => (
                <AgentStatusRow key={agent.key} agent={agent} />
              ))}
            </ul>
          </GlassPanel>

          <GlassPanel padded={false}>
            <div className="px-5 pt-5">
              <PanelHeader
                title="Signaler fra omverdenen"
                subtitle="Nyheter og hendelser Nova har fanget opp — lag forslag rett herfra"
                icon={<Radar size={16} />}
                action={
                  <Link to="/agenter" className="text-accent text-xs hover:underline">
                    Alle signaler
                  </Link>
                }
              />
            </div>
            <div className="px-3 pb-3">
              <SignalsPanel take={5} />
            </div>
          </GlassPanel>

          <GlassPanel padded={false}>
            <div className="px-5 pt-5">
              <PanelHeader
                title="Aktivitet"
                subtitle="Alt som skjer i Nova, i sanntid"
                icon={<Inbox size={16} />}
                action={
                  <Link to="/aktivitet" className="text-accent text-xs hover:underline">
                    Hele loggen
                  </Link>
                }
              />
            </div>
            <ol className="space-y-3 px-5 pb-5">
              {data?.activity.map((entry) => (
                <li key={entry.id} className="border-hairline relative border-l pl-4">
                  <span
                    aria-hidden
                    className={cn(
                      'absolute -left-[4.5px] top-1.5 h-2 w-2 rounded-full',
                      entry.actorType === 'AGENT'
                        ? 'bg-violet'
                        : entry.actorType === 'SYSTEM'
                          ? 'bg-ink-soft'
                          : 'bg-accent',
                    )}
                  />
                  <p className="text-ink-soft text-sm leading-snug">{entry.message}</p>
                  <p className="text-ink-soft/70 mt-0.5 text-[11px]">
                    {formatRelative(entry.createdAt)}
                  </p>
                </li>
              ))}
            </ol>
          </GlassPanel>
        </div>
      </div>

      <ContentDrawer contentId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function AgentStatusRow({ agent }: { agent: AgentDto }) {
  const running = Boolean(agent.runningRun);
  const failed = agent.lastRun?.status === 'FAILED';

  return (
    <li>
      <Link
        to="/agenter"
        className="hover:bg-raised/60 flex items-start gap-3 rounded-lg px-2 py-2.5 transition"
      >
        <span
          aria-hidden
          className={cn(
            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
            running
              ? 'animate-orb-pulse bg-violet shadow-glow-sm'
              : !agent.isEnabled
                ? 'bg-ink-soft/50'
                : failed
                  ? 'bg-danger'
                  : 'bg-success',
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="text-ink truncate text-sm">{agent.name}</span>
            <span className="text-ink-soft shrink-0 text-[11px]">
              {running
                ? 'Kjører nå'
                : agent.lastRun
                  ? formatRelative(agent.lastRun.startedAt)
                  : 'Ikke kjørt'}
            </span>
          </span>
          <span className="text-ink-soft mt-0.5 block truncate text-[11px]">
            {running
              ? (agent.runningRun?.summary ?? 'Arbeider …')
              : !agent.isEnabled
                ? 'Slått av'
                : failed
                  ? `${AGENT_RUN_STATUS_LABELS.FAILED}: ${agent.lastRun?.error ?? 'ukjent feil'}`
                  : (agent.lastRun?.summary ?? agent.scheduleLabel)}
          </span>
          {agent.nextRunAt && !running && (
            <span className="text-ink-soft/60 mt-0.5 block text-[11px]">
              Neste: {formatDateTime(agent.nextRunAt)}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}
